import React from 'react';
import {Grid, withStyles} from '@material-ui/core';
import visualizationDataService from '../services/visualizationService';
import VisualizationCanvas from '../components/VisualizationCanvas';
import {INRIA_COLOR, OTHER_COLOR, programColors} from '../constants/colors';
import {statusOptions, statusOptionsAllChecked} from '../constants/status';
import {collabTypesOptions, collabAreasAllChecked} from '../constants/collabTypes';
import {getLabels} from '../constants/labels';
import slugify from 'slugify';
import {connect} from 'react-redux';
import {languageActions} from '../actions/language.actionCreators';
import {legendActions} from '../actions/legend.actionCreators';
import {bindActionCreators} from 'redux';
import {ALL_CATEGORIES} from '../constants/categories';

import { Auth } from 'aws-amplify';


const OTHER_LINK_TYPE = 'other';

const INITIAL_START_DATE = new Date(2012, 0, 1)
const START_DATE_AFTER_SEARCH = new Date(2012, 0, 1)

const styles = theme => ({
    root: {
        padding: '0px 20px 0px 20px'
    },
    activeButton: {
        textDecoration: 'underline'
    },
    tabs: {
        paddingLeft: "6px",
    },
    tab: {
        marginTop: '10px',
        color: 'rgba(0,0,0,0.6)',
        display: 'inline-block',
        padding: '10px',
        borderBottom: '3px solid rgba(0,0,0,0.2)',
        borderRadius: '10px 10px 0 0',
        cursor: 'pointer',
        backgroundColor: '#eeeeee',
        '&:hover, &:active': {
            backgroundColor: '#eceff1',
            borderBottom: '1px solid rgba(0,0,0,0.5)'
        },
        '&.Mui-selected': {
            backgroundColor: 'rgba(0,0,0,0.2)',
        }
    },
    languages: {
        position: 'relative',
    },
});

export function getEntityCountry(entity) {
    const zone = entity.zone;
    if (!zone) return null;
    if (zone.type === 'pais') return entity.zone;
    let country_zone = {};
    (zone.zone_hierarchy || []).forEach(t => {
        if (t.type === 'pais') {
            country_zone = t;
        }
    });
    return country_zone || entity.zone;
}

export function getEntityContinent(entity) {        
    if (entity.zone_hierarchy?.[0] && entity.zone_hierarchy[0].type === 'continent') {
        return entity.zone_hierarchy[0];  
    }
    const zone = entity.zone;
    if (!zone) return null;
    if (!zone.zone_hierarchy) return null;    
    if (zone.zone_hierarchy[0].type === 'continent') return entity.zone.zone_hierarchy[0];   
    return null;
}

// Get categories structure for visualization
export function getCategories(entities_data, language="en") {
    const nameKey = `name_${language}`;
    let categories = [];
    entities_data.forEach(entity => {
        const country = getEntityCountry(entity);
        const continent = getEntityContinent(entity);
        if (entity.name.includes('Inria Chile')) {
            categories.push({id: entity.id.toString(), name: entity.name, color: INRIA_COLOR});
        }
        else if (country && continent && !categories.find(c => c.id === `${continent.natural_name}.${country.natural_name}`)) {
            const category = {id: `${continent.natural_name}.${country.natural_name}`, name: country[nameKey], short_name: country.short_name, zone_hierarchy: country.zone_hierarchy};
            category['color'] = OTHER_COLOR;
            categories.push(category);
        }
        if (continent && !categories.find(c => c.id === continent.natural_name)) {
            const category = {id: continent.natural_name, name: continent[nameKey], short_name: continent.short_name};
            categories.push(category);
        }
    });
    if (!categories.find(c => c.name === 'Inria Chile')) {
        categories.push({id: 'inria-chile', name: 'Inria Chile', highlight: true, color: INRIA_COLOR});
    }

    return categories;
}

// Remove unused categories (Categories without children)
export function filterUsedCategories(categories, entities) {
    return categories.filter(c => {
        return entities.find(e => e?.id?.startsWith(c.id));
    });
}

// Determine if 2 entities have the same parent
export function areSiblings(entity1, entity2) {
    const hierarchy1 = entity1.id.split('.');
    const hierarchy2 = entity2.id.split('.');
    const levels = hierarchy1.length;
    if (hierarchy2.length !== levels) return false;
    for (let i = 0; i < levels - 1; i++) {
        if (hierarchy1[i] !== hierarchy2[i]) {
            return false;
        }
    }
    return true;
}

function sortBySortKey(entity1, entity2){
    const key1 = entity1.sortKey !== undefined ? entity1.sortKey : entity1.name;
    const key2 = entity2.sortKey !== undefined ? entity2.sortKey : entity2.name;
    return key1.localeCompare(key2);
}

export function sortEntities(entity1, entity2, entities) {
    if (areSiblings(entity1, entity2)) { // Sort by name
        return entity1.name.localeCompare(entity2.name);
    } else {
        const hierarchy1 = entity1.id.split('.');
        const hierarchy2 = entity2.id.split('.');
        let index_diff = -1;
        for (let i = 0; i < Math.min(hierarchy1.length, hierarchy2.length); i++) {
            if (hierarchy1[i] !== hierarchy2[i]) {
                index_diff = i;
                break;
            }
        }
        if (index_diff <= 0) { // Sort by id
            return entity1.id.localeCompare(entity2.id);
        }
        const parent_id1 = hierarchy1.slice(0, index_diff + 1).join('.');
        const parent_id2 = hierarchy2.slice(0, index_diff + 1).join('.');
        const parent1 = entities.find(e => e.id === parent_id1);
        const parent2 = entities.find(e => e.id === parent_id2);
        if (!parent1 || !parent2) return 0;
        // Sort by parent name
        return parent1.name.localeCompare(parent2.name);
    }
}

// Get entities structure for visualization
export function getEntities(categories, entities_data, language="en") {
    let entities = [];
    let inriaChile = {};     
    // Get entities, building their hierarchical ids
    entities_data.forEach(entity => {
        if (!categories.find(c => c.id === entity.id.toString())) { // Categories are already entities
            const entityKeys = getHierarchicalKeys(categories, entities_data, entity, [entity.id.toString(), entity.name], language);
            const entity_object = {id: entityKeys[0], sortKey: slugify(entityKeys[1]), name: entity.name, short_name: entity.short_name};
            entities.push(entity_object);
        }
    });
    categories.forEach(c => {
        const entityKeys = getHierarchicalKeys(categories, entities_data, c, [c.id.toString(), c.name], language);
          
        const category_entity = {id: c.id, name: c.name, short_name: c.short_name, sortKey: slugify(entityKeys[1])};
        if (c.name === 'Inria Chile') {
            category_entity['highlight'] = true; // Set as a highlighted entity
            inriaChile = category_entity; // Separate inriaChile to set as entity later
        } else if (entities.find(e => e.id.startsWith(c.id))) { //"Does the category have children?"
            category_entity['category_title'] = true; // Set as a category (a separated arc)          
            entities.push(category_entity);
        }
    });

    // Sort entities alphabetically inside each category (sorting categories alphabetically too)
    entities = entities.slice(0, entities.length).sort(sortBySortKey);
    // Separate Inria and french institutions
    let inriaInstitutions = [];
    let otherFrenchInstitutions = [];
    let otherEuropeInstitutions = [];
    let current_inriaInstitution_id = '';
    let current_frenchInstitution_id = '';
    let current_europeInstitution_id = '';
    const isEurope = (e) => e.name === "Europe" || e.name === "Europa"
    const isFrance = (e) => e.name === "France" || e.name === "Francia"
    const france_id = entities.find(e => isFrance(e))?.id
    entities.forEach(entity => {
        if (entity.name.trim() === 'Inria') {
            current_inriaInstitution_id = entity.id;
        }
        else if (isEurope(entity) && categories.find(c => c.name === entity.name)) {
            current_europeInstitution_id = entity.id;
        }
        else if (isFrance(entity) && categories.find(c => c.name === entity.name)) {
            current_frenchInstitution_id = entity.id;
        }
        if (current_inriaInstitution_id !== '' && entity.id.startsWith(current_inriaInstitution_id)) {
            inriaInstitutions.push(entity);

        } else if (current_europeInstitution_id !== '' && entity.id.startsWith(current_europeInstitution_id) && !entity.id.includes(france_id)) {
            otherEuropeInstitutions.push(entity);
        } else if (current_frenchInstitution_id !== '' && entity.id.startsWith(current_frenchInstitution_id) && (entity.id.includes(france_id))) {
            otherFrenchInstitutions.push(entity);
        }
    });

    // Leave europe institutions first (left side of the wheel), France first and Inria first
    let firstEntities = [inriaChile]
        .concat(otherEuropeInstitutions[0] ? [otherEuropeInstitutions[0]] : []) // Europe continent
        .concat((otherFrenchInstitutions[0] ? [otherFrenchInstitutions[0]] : [])) // France country
        .concat(inriaInstitutions) // French Inria institutions
        .concat(otherFrenchInstitutions.slice(1))  // The rest of french institutions
        .concat(otherEuropeInstitutions.slice(1));  // The rest of europe institutions
    entities = firstEntities.concat(entities.filter(e => !firstEntities.includes(e)));   
  
    if (entities.length === 1) return [];
    return entities;
}

export function getHierarchicalId(categories, entities_data, entity, current_result) {
    if (categories.find(c => c.name === entity.name)) {
        return current_result;
    }
    const parent_id = entity.parent;
    const parent = entities_data.find(e => e.id === parent_id);
    if (parent_id === null || parent === undefined) {
        const country = getEntityCountry(entity);
        if (country) return `${country.natural_name}.${current_result}`;
        return current_result;
    }
    return getHierarchicalId(categories, entities_data, parent, `${parent_id}.${current_result}`);
}

export function getHierarchicalKeys(categories, entities_data, entity, current_result, language) {
    const parent_id = entity.parent;
    const parent = entities_data.find(e => e.id === parent_id);
    
    if (entity.id.toString().includes(".") && entity.id.split(".").length === 2) {       
        const continent = getEntityContinent(entity);       
        if (continent) {
            const countryKey = `name_${language}`;
            return [
                `${continent.natural_name}.${current_result[0]}`,
                `${continent[countryKey]}.${current_result[1]}`
            ];
        }
    }

    if (parent_id === null || parent === undefined) {
        const country = getEntityCountry(entity);
        const continent = getEntityContinent(entity);
        if (country && continent) {
            const countryKey = `name_${language}`;            
            return [
                `${continent.natural_name}.${country.natural_name}.${current_result[0]}`,
                `${continent[countryKey]}.${country[countryKey]}.${current_result[1]}`
            ];
        }
        return current_result;
    }
    const tail = [
        `${parent_id}.${current_result[0]}`,
        `${parent.name}.${current_result[1]}`
    ];
    return getHierarchicalKeys(categories, entities_data, parent, tail, language);
}

// Get collaboration types for visualization
export function getCollabTypes(programs) {
    let color_index_array = {
        'Inria Instruments': 0,
        'Public Instruments': 0,
        'Private Instruments': 0,
        'Other': 0
    };

    let formattedPrograms = (programs || []).map(program => {
        const hierarchicalId = getHierarchicalProgramId(programs, program, program.id.toString());
        const idArray = hierarchicalId.split('.');
        let color = undefined;

        if (idArray.length <= 2) {
            const programNameKeys = [program.name, program.name_es, program.name_en];
            let foundKey = '';

            const programHasMatchingColor = programNameKeys.some(name => {
                if (name in programColors) {
                    foundKey = name;
                    return true; // Stop iteration if a match is found
                }
                return false;
            });

            if (programHasMatchingColor) {
                // If the program has its color set by one of its names
                color = programColors[foundKey];
            } else {
                // If the program does not have a color set yet, it will use one of the spare ones.
                const parent_id = parseInt(idArray[0], 10);
                const parent = programs.find(e => e.id === parent_id);

                if (parent) { // Ensure parent is found
                    const parentNameKeys = [parent.name, parent.name_es, parent.name_en];
                    let foundKey = '';

                    const parentHasMatchingColor = parentNameKeys.some(name => {
                        if (name in programColors) {
                            foundKey = name;
                            return true; // Stop iteration if a match is found
                        }
                        return false;
                    });

                    if (parentHasMatchingColor) {
                        const color_index = color_index_array[parent.name_en];
                        color = programColors[foundKey][color_index];
                        color_index_array[parent.name_en] += 1;
                    }
                }
            }
        }

        return {
            id: hierarchicalId,
            originalId: program.id.toString(),
            name: program.name,
            order: program.order,
            color
        };
    });

    /* Set colors for collabTypes of level 3 (third depth level in legend),
    according to their parents of level 2 */
    formattedPrograms = formattedPrograms.map(p => {
        if (!p.color) {
            const parent = formattedPrograms.find(o =>
                o.color && p.id.startsWith(o.id) && o.id?.split('.').length === 2
            );
            return {...p, color: parent?.color};
        }
        return p;
    });

    return formattedPrograms.slice(0, formattedPrograms.length).sort((a, b) => {
        return a.order > b.order ? 1 : -1;
    });
}

function getHierarchicalProgramId(programs, program, current_result, workArea="science") {
    const parent_id = program[`${workArea}_parent`];
    const parent = programs.find(p => p.id === parent_id);
    if (program[`${workArea}_parent`] === null || parent === undefined) {
        return current_result;
    }
    return getHierarchicalProgramId(programs, parent, `${parent_id}.${current_result}`, workArea);
}

function getAction(actionId, actions) {
    return (actions || []).find(a => a.id === actionId);
}

// Get links structure for visualization
export function getLinks(collaborations, entities, actions, collab_types) {
    let links = [];
    (collaborations || []).forEach(col => {
        // Getting hierarquical ids
        let entity1_hierarchical_id = '';
        let entity2_hierarchical_id = '';
        entities.forEach(e => {
            if (e.id === col[0].toString() || e.id?.endsWith(`.${col[0]}`)) {
                entity1_hierarchical_id = e.id;
            } else if (e.id === col[1].toString() || e.id?.endsWith(`.${col[1]}`)) {
                entity2_hierarchical_id = e.id;
            }
        });
        const action = getAction(col[2], actions);
        const prog = collab_types.find(c => c.id === action?.external_program.toString() || c.id.endsWith(`.${action?.external_program.toString()}`))?.id;

        if (entity1_hierarchical_id !== '' && entity2_hierarchical_id !== '') {
            links.push({
                id1: entity1_hierarchical_id,
                id2: entity2_hierarchical_id,
                type: prog || OTHER_LINK_TYPE
            });
        }
    });
    return links;
}

function getActionTableData(actions, programs, entities, status) {
    return actions.map(action => {
        const program = programs.find(p => p.id === action.external_program) || {};
        const partners = entities.filter(
            e => action.partners.includes(e.id)
        ).map(e => e.name).sort(function (a, b) {
            return a.toLowerCase().localeCompare(b.toLowerCase());
        }).join(', ');
        const partnersNames = action.partners.map(p =>
            (entities || []).find(e => e.id.toString() === p.toString() || (e.id?.toString()).endsWith(`.${p}`))?.name
        );
        return {
            name: action.name,
            description: action.description,
            submitDate: action.submitted_date,
            startDate: action.start_date,
            resultDate: action.results_date,
            endDate: action.end_date,
            program: program.name,
            programId: program.id?.toString(),
            statusId: action.status,
            status: status[action.status],
            partnersArray: action.partners,
            partners,
            partnersNames,
            people: action.people
        };
    });
}

function getWorkAreaInitialState(signed_user, searching=false) {
    const fromDate = searching ? START_DATE_AFTER_SEARCH : INITIAL_START_DATE;
    const statuses = searching ? statusOptionsAllChecked : statusOptions;
    const collabTypes = collabTypesOptions;
    const collab_areas = searching ? collabAreasAllChecked : getCollabAreas(collabTypes);
    return {
        categories: [],
        entities: [],
        collab_types: [],
        collab_areas,
        links: [],
        fromDate,
        toDate: new Date(),
        states: getCollabStates(statuses, signed_user),
        actions: [],
        loading: false,
        clickedEntity: '',
        work_area_amounts: [0,0],
        status_amounts: [0,0,0,0,0,0]
    };
}

// Get collaboration types: all collabs, scientifics collabs, innovation collabs
function getCollabAreas(collabTypes) {
    let collabs_list = [];
        for (var i =0; i < collabTypes?.length; i ++){
            let collab_obj = {}
            for (var j=0; j < Object.keys(collabTypes[i]).length; j ++){
                collab_obj[Object.keys(collabTypes[i])[j]] = collabTypes[i][Object.keys(collabTypes[i])[j]]
            }
            collabs_list.push(collab_obj);
        }
    return collabs_list;
}

function getCollabStates(states=statusOptions, signed_user) {
    let states_copy = [];
    for (var i = 0; i < states.length; i ++) {
        let state_obj = {}
        for (var j = 0; j < Object.keys(states[i]).length; j ++) {
            state_obj[Object.keys(states[i])[j]] = states[i][Object.keys(states[i])[j]]
        }
        states_copy.push(state_obj);
    }
    if (signed_user) {
        return states_copy;
    }
    // Only public states
    return (states_copy || []).filter(s =>
        (s.label !== "Submitted")
        && (s.label !== "Cancelled")
        && (s.label !== "Unsuccessful")
    );
}

class VisualizationContainer extends React.Component {
    state = {
        signed_user: false,
        language: this.props.language || "en", // language is a redux stored prop
        requestData: {},
        both_data: getWorkAreaInitialState(false),
        tab_value: 0
    };

    setInitialStates(signed_user) {
        this.setState({
            signed_user,
            both_data: getWorkAreaInitialState(signed_user),
        });
        this.loadData();
    }

    componentStart() {
        Auth.currentAuthenticatedUser()
            .then(() => {
                this.setInitialStates(true);
            })
            .catch(() => {
                this.setInitialStates(false);
            });
    }

    componentDidMount() {
        this.componentStart();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.search_event !== this.props.search_event) {
            this.handleResetFilters();
        }
        if (prevProps.language_change !== this.props.language_change) {
            this.setLanguage(this.props.language_change);
        }
    }

    getWorkArea() {
        let work_area_list = [];
        const work_area_checked = this.state.both_data.collab_areas;
        for (let i=0; i < work_area_checked.length; i++){
            const w = work_area_checked[i];
            if (w.checked) {
                work_area_list.push(w.id)
            }
        };

        let work_area_string = work_area_list.join();
        if (work_area_list.length && work_area_string[0] === ",") {
            work_area_string = work_area_string.substring(1);
        }
        if (work_area_string.split(',').length >= 2) {
            return null;
        }
        return work_area_string === "" ? "-" :  work_area_string;
    }

    loadData() {
        const {language, signed_user} = this.state;
        const work_area = this.getWorkArea();
        this.setState({both_data: {...this.state.both_data, loading: true}});
        return visualizationDataService.getAll(
            work_area,
            this.state.both_data, signed_user, {
                search_category: ALL_CATEGORIES,
                search_values: this.props.search_values.map(
                    v=> language === "en" ? v.name_en : v.name_es
                ),
                language
            })
            .then(res => {
                this.setState(state => ({
                    requestData: {
                        ...res.data,
                        states: getCollabStates(res.data.states, state.signed_user),
                        collab_areas: getCollabAreas(res.data.collab_areas) || {}
                    }
                }), () => this.updateDataForVisualization());
            })
            .catch(err => {
                this.setState({both_data: {
                    ...this.state.both_data,
                    loading: false,
                    states : getCollabStates(this.state.both_data.states, this.state.signed_user),
                    collab_areas: getCollabAreas(this.state.both_data.collab_areas) || {}
                }})
            }); // TODO: Make an load error message
    }

    setLanguage(language) {
        const redux_actions = this.props.actions;
        if (language !== this.state.language) {
            this.setState(state => ({
                    language,
                    both_data: {...this.state.both_data, loading: true},
                }),
                () => {
                    redux_actions.saveLanguage(language);
                    this.updateDataForVisualization();
                });
        }
    }

    updateDataForVisualization() {
        const currentStates = this.state.both_data.states;
        const {requestData, language} = this.state;
        const nameKey = `name_${language}`;
        const descriptionKey = `description_${language}`;
        const labelKey = `label_${language}`;
        const od = requestData;
        const data = {
            ...od,
            actions: (od.actions || []).map(a => ({...a, name: a[nameKey]})),
            programs: (od.programs || []).map(p => ({...p, name: p[nameKey], description: p[descriptionKey]})),
            states: (currentStates || []).map(s => ({...s, label: s[labelKey]})),
            collab_areas: (this.state.both_data.collab_areas || []).map(s => ({...s, label: s[labelKey]}))
        };
        this.getDataForVisualization(data, language);
    }


    getDataForVisualization(data, language) {
        let actions = data.actions;
        let entities_data = data.entities;       
        // Remove entities with no zone and parent
        entities_data = (entities_data || []).filter(ed => ed.zone || ed.parent);

        // chart categories: top level group elements (countries and Inria Chile)
        let categories = getCategories(entities_data || [], language);
        let entities = getEntities(categories, entities_data || [], language);

        categories = filterUsedCategories(categories, entities) || [];
        // collab_types must have at most 10 elements, otherwise, links will not display
        const collab_types = getCollabTypes(data.programs);
        let links = getLinks(data.collaborations, entities, actions, collab_types);

        const status = {};
        const nameKey = `name_${language === 'en' ? 'eng' : language}`;
        (data.status || []).forEach(s => {
            status[s.id] = s[nameKey];
        });

        actions = getActionTableData(actions, data.programs, entities_data, status);

        let clickedEntity = this.state.both_data.clickedEntity;
        if (clickedEntity !== '' && !entities.some(e => e.id === clickedEntity)) {
            clickedEntity = '';
        }

        this.setState({
                both_data: {
                    ...this.state.both_data,
                    loading: false,
                    states: data.states,
                    collab_areas: data.collab_areas,
                    entities, categories, collab_types, links, actions, clickedEntity,
                    work_area_amounts: data.work_area_amounts,
                    status_amounts: data.status_amounts
                }
        });
    }

    resetLegend() {
        const {legend_checks, actions} = this.props;
        const new_eye_checks = {};
        for (const item in legend_checks) {
            new_eye_checks[item] = true;
        }
        actions.saveChecks(new_eye_checks)
    }

    handleResetFilters() {
        this.resetLegend();
        const searching = this.props.search_values.length !== 0;
        this.setState({
            both_data: getWorkAreaInitialState(this.state.signed_user, searching)
        }, () => {
            this.loadData();
        })
    }

    handleFilterChange() {
        return (value, filterId) => {
            this.setState({
                both_data: {
                    ...this.state.both_data,
                    [filterId]: value
                }
            }, () => this.loadData());
        };
    }

    onStatusChange() {
        return (item, checked) => {
            let states = this.state.both_data.states
            let all_checked = true
                // Case all status checkbox selected
                if (item.id === "") {
                    for (var c = 0; c < states.length; c ++) {
                        states[c].checked = checked;
                    }
                }
                // Case one checkbox selected/unselected
                else {
                    for (var e = 0; e < states.length; e ++) {
                        if (states[e].label === item.label) {
                            states[e].checked = checked;
                        }
                        else if(states[e].id==="" && checked===false) {
                            states[e].checked = false;
                        }
                        // states[e] es la otra area
                        if(states[e].id!==""){
                            all_checked= all_checked&&states[e].checked
                        }
                    }                
                    if(all_checked){
                        states[0].checked = checked
                    }
                }

            this.setState({
                both_data: {
                    ...this.state.both_data, states
                }
            }, () => this.loadData());
        };
    }

    onCollabAreasChange() {
        return (item, checked) => {
            let collab_areas =  this.state.both_data.collab_areas

            // Case all collaborations checkbox selected
            if (item.id === "") {
                for (var c = 0; c < collab_areas.length; c ++) {
                    collab_areas[c].checked = checked;
                }
            }
            // Case one checkbox selected/unselected
            else {
                for (var e = 0; e < collab_areas.length; e ++) {
                    if (collab_areas[e].label === item.label) {
                        collab_areas[e].checked = checked;
                    }
                    else if(collab_areas[e].id==="" && checked===false) {
                        collab_areas[e].checked = false;
                    }
                    else {   // collab_areas[e] es la otra area
                        if (collab_areas[e].checked && checked) {
                            collab_areas[0].checked = checked // all collabs
                        }
                    }
                }
            }
            this.setState({
                both_data: {
                    ...this.state.both_data, collab_areas
                }
            }, () => this.loadData());
        };
    }

    handleLabelClick() {
        return (event, entity) => {
            let value = entity.id;
            if (this.state.both_data.clickedEntity === entity.id) {
                value = '';
            }
            this.setState({
                both_data: {
                    ...this.state.both_data,
                    clickedEntity: value
                }
            });
        };
    }

    handleChange = (_, tab_value) => {
        this.setState(
            {tab_value},
            () => {
                this.loadData();
            }
        );
    }

    render() {
        const {classes} = this.props;
        const {language} = this.state;
        const labels = getLabels(language);
        return (<div className={classes.root}>
            <Grid container justifycontent="flex-end" spacing={1}>
                <Grid item container xs={12}>
                    <VisualizationCanvas
                        signed_user={this.state.signed_user}
                        language={language}
                        data={this.state.both_data}
                        title={labels.filtersTitle}
                        handleFilterChange={this.handleFilterChange()}
                        onStatusChange={this.onStatusChange()}
                        handleLabelClick={this.handleLabelClick()}
                        loading={this.state.both_data.loading}
                        onReset={() => this.handleResetFilters()}
                        onCollabAreasChange={this.onCollabAreasChange()}
                    />
                </Grid>
            </Grid>
        </div>);
    }
}

const mapStateToProps = state => {
    return {
        search_values: state.searcherReducer.search_values,
        legend_checks: state.legendReducer.legend_checks,
        language: state.languageReducer.language
    };
};

const mapDispatchToProps = dispatch => {
    return {
        actions: bindActionCreators({...languageActions, ...legendActions}, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(VisualizationContainer));
